// Sdílené kusy UI a datové vrstvy Spižírny (suroviny, polotovary, produkty,
// přídavky) a Balírny.
//
// Stránky `MaterialoveKarty.jsx` a `ProduktKvalita.jsx` vznikly jako dva moduly
// a každá si nesla vlastní kopii těch samých patnácti pomocníků — `api`, styly,
// strom kategorií. Byly bajt po bajtu shodné, takže se zatím nerozešly, ale nic
// tomu nebránilo a sdílené komponenty (modal jednotek, nahrávání médií) si je
// musely odvozovat potřetí. Tady je pravda; vzorem je `lib/uiTokens.js`, který
// takhle sjednotil finanční moduly.

import { SKLO_SVETLE } from './uiTokens';

// Akcent modulu. Balírna má v hlavičce vlastní zelenou, ale tlačítka a pilulky
// zůstávají jantarové jako ve Spižírně — jsou to tytéž karty položek.
export const AKCENT = '#F59E0B';
export const AKCENT_BG = 'rgba(245,158,11,0.12)';

export const csrf = () => document.querySelector('meta[name="csrf-token"]')?.content;

export const api = async (url, method = 'GET', body) => {
    const opts = { method, headers: { Accept: 'application/json', 'X-CSRF-TOKEN': csrf() } };
    if (body) { opts.headers['Content-Type'] = 'application/json'; opts.body = JSON.stringify(body); }
    const r = await fetch(url, opts);
    if (!r.ok) { const e = await r.json().catch(() => ({})); throw e; }
    return r.json();
};

/**
 * Lokální patch uzlu ve stromu kategorií — vrací nový strom (immutable).
 *
 * Úprava kategorie je jen změna polí na jednom uzlu, takže se nemusí čekat na
 * server ani přenačítat strom. Kdyby PUT selhal, zavolá se refetch a stav se
 * srovná podle serveru.
 */
export const patchKatNode = (nodes, id, patch) => (nodes || []).map(n =>
    n.id === id
        ? { ...n, ...patch, children: n.children }
        : (n.children?.length ? { ...n, children: patchKatNode(n.children, id, patch) } : n)
);

/** Strom kategorií na plochý seznam s `_depth` — pro odsazení v selectech. */
export const flattenKatTree = (nodes, depth = 0) => {
    let result = [];
    for (const node of nodes || []) {
        result.push({ ...node, _depth: depth });
        if (node.children?.length) result = result.concat(flattenKatTree(node.children, depth + 1));
    }
    return result;
};

/**
 * Styly modulu odvozené od tématu. Vrací i `renderFormField` už navázané na
 * `secondaryColor`, aby volající místa zůstala beze změny.
 */
export function stylySpizirny(isDark, akcent = AKCENT) {
    const secondaryColor = isDark ? 'rgba(255,255,255,0.5)' : 'var(--text-secondary)';
    const mutedColor = isDark ? 'rgba(255,255,255,0.35)' : 'var(--text-muted, #94a3b8)';

    const cardStyle = isDark ? {
        background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)',
        backdropFilter: 'blur(40px)', WebkitBackdropFilter: 'blur(40px)',
        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.04), 0 0 0 0.5px rgba(255,255,255,0.03)',
    } : SKLO_SVETLE;
    // Mléčné sklo pro modaly (neprůhledné pozadí + rozostření)
    const glassCard = {
        background: isDark ? 'rgba(24,22,32,0.92)' : 'rgba(255,255,255,0.94)',
        backdropFilter: 'blur(28px) saturate(1.4)', WebkitBackdropFilter: 'blur(28px) saturate(1.4)',
        border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)'}`,
        boxShadow: '0 24px 64px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.08)',
    };
    const inputStyle = {
        background: isDark ? 'rgba(255,255,255,0.06)' : 'var(--surface-base)',
        border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'var(--border-default)'}`,
        color: 'var(--text-primary)', borderRadius: 6, padding: '8px 12px', fontSize: 13,
        width: '100%', outline: 'none', colorScheme: isDark ? 'dark' : 'light',
    };
    const selectStyle = { ...inputStyle, cursor: 'pointer' };
    const optionStyle = { background: isDark ? '#1C1C20' : '#fff', color: isDark ? '#e2e8f0' : '#1e293b' };
    const btnPrimary = {
        background: akcent, color: '#fff', border: 'none', borderRadius: 6,
        padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
        display: 'flex', alignItems: 'center', gap: 6,
    };
    const btnSecondary = {
        background: isDark ? 'rgba(255,255,255,0.08)' : 'var(--surface-base)',
        color: secondaryColor, border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'var(--border-default)'}`,
        borderRadius: 6, padding: '8px 16px', fontSize: 13, fontWeight: 500,
        cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
    };
    const btnDanger = { ...btnPrimary, background: '#EF4444' };
    const thStyle = { padding: '10px 16px', textAlign: 'left', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', color: mutedColor, fontWeight: 600 };
    const tdStyle = { padding: '12px 16px', fontSize: 13, color: 'var(--text-primary)' };

    const renderFormField = (label, children) => (
        <div><label className="block text-xs font-medium mb-1" style={{ color: secondaryColor }}>{label}</label>{children}</div>
    );

    return {
        secondaryColor, mutedColor, cardStyle, glassCard, inputStyle, selectStyle,
        optionStyle, btnPrimary, btnSecondary, btnDanger, thStyle, tdStyle, renderFormField,
    };
}
